import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@mantine/core';
import { IconSettingsCode } from '@tabler/icons-react';
import {
  AssetBundleItem,
  usePostApiBotProviderAssetBundleUploadFile,
  useDeleteApiBotProviderAssetBundleDeleteFile,
  usePostApiBotProviderAssetBundleCreateFolder,
  useDeleteApiBotProviderAssetBundleDeleteFolder,
  usePutApiBotProviderAssetBundleSaveTextFile,
  usePostApiBotProviderAssetBundleRenameFile,
  usePostApiBotProviderAssetBundleDuplicateFile,
  usePostApiBotProviderAssetBundleRenameFolder,
  usePostApiBotProviderAssetBundleDuplicateFolder,
} from '@/API';
import { GetWebBotBundleUrl, GetBotProviderAssetBundleDownloadZipUrl } from '@/APIURL';
import {
  VirtualFileExplorerModal,
  type VirtualFileExplorerAdapter,
} from '@/Components/VirtualFileExplorer';

export interface AssetBundleExplorerModalProps {
  opened: boolean;
  onClose: () => void;
  workspaceId: string;
  botProviderId: string;
  bundle: AssetBundleItem;
  onBundleUpdated?: (updatedBundle: AssetBundleItem) => void;
  onOpenManifestEditor?: () => void;
  mode?: 'manage' | 'pick';
  allowedExtensions?: string[];
  selectedFilePath?: string | null;
  onSelectConfirm?: (filePath: string) => void;
  title?: string;
}

export function AssetBundleExplorerModal({
  opened,
  onClose,
  workspaceId,
  botProviderId,
  bundle: initialBundle,
  onBundleUpdated,
  onOpenManifestEditor,
  mode = 'manage',
  allowedExtensions = [],
  selectedFilePath = null,
  onSelectConfirm,
  title,
}: AssetBundleExplorerModalProps) {
  const [currentBundle, setCurrentBundle] = useState<AssetBundleItem>(initialBundle);

  useEffect(() => {
    setCurrentBundle(initialBundle);
  }, [initialBundle]);

  const uploadFileMutation = usePostApiBotProviderAssetBundleUploadFile();
  const deleteFileMutation = useDeleteApiBotProviderAssetBundleDeleteFile();
  const createFolderMutation = usePostApiBotProviderAssetBundleCreateFolder();
  const deleteFolderMutation = useDeleteApiBotProviderAssetBundleDeleteFolder();
  const saveTextFileMutation = usePutApiBotProviderAssetBundleSaveTextFile();
  const renameFileMutation = usePostApiBotProviderAssetBundleRenameFile();
  const duplicateFileMutation = usePostApiBotProviderAssetBundleDuplicateFile();
  const renameFolderMutation = usePostApiBotProviderAssetBundleRenameFolder();
  const duplicateFolderMutation = usePostApiBotProviderAssetBundleDuplicateFolder();

  const isOperating =
    uploadFileMutation.isPending ||
    deleteFileMutation.isPending ||
    createFolderMutation.isPending ||
    deleteFolderMutation.isPending ||
    saveTextFileMutation.isPending ||
    renameFileMutation.isPending ||
    duplicateFileMutation.isPending ||
    renameFolderMutation.isPending ||
    duplicateFolderMutation.isPending;

  const adapter: VirtualFileExplorerAdapter = useMemo(() => {
    const bundleName = currentBundle.bundleName || '';

    return {
      isOperating,
      getFileContent: async (filePath: string) => {
        const url = GetWebBotBundleUrl(botProviderId, `${bundleName}/${filePath}`);
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to read file');
        return res.text();
      },
      saveFile: async (filePath: string, content: string) => {
        await saveTextFileMutation.mutateAsync({
          params: { workspaceId, botProviderId, bundleName, filePath },
          data: { content },
        });
      },
      uploadFile: async (relativePath: string, file: File) => {
        const res = await uploadFileMutation.mutateAsync({
          params: {
            workspaceId,
            botProviderId,
            bundleName,
            relativePath,
          },
          data: { file },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      deleteFile: async (filePath: string) => {
        const res = await deleteFileMutation.mutateAsync({
          params: { workspaceId, botProviderId, bundleName, fileName: filePath },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      renameFile: async (oldFilePath: string, newFileName: string) => {
        const res = await renameFileMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { bundleName, oldFilePath, newFileName },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      duplicateFile: async (sourceFilePath: string, targetFileName: string) => {
        const res = await duplicateFileMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { bundleName, sourceFilePath, targetFileName },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      createFolder: async (folderPath: string) => {
        const res = await createFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { bundleName, folderPath },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      deleteFolder: async (folderPath: string) => {
        const res = await deleteFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId, bundleName, folderPath },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      renameFolder: async (oldFolderPath: string, newFolderName: string) => {
        const res = await renameFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { bundleName, oldFolderPath, newFolderName },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      duplicateFolder: async (sourceFolderPath: string, targetFolderName: string) => {
        const res = await duplicateFolderMutation.mutateAsync({
          params: { workspaceId, botProviderId },
          data: { bundleName, sourceFolderPath, targetFolderName },
        });
        if (res?.data) {
          setCurrentBundle(res.data);
          onBundleUpdated?.(res.data);
        }
      },
      getFileUrl: (filePath: string) =>
        GetWebBotBundleUrl(botProviderId, `${bundleName}/${filePath}`),
      getDownloadZipUrl: () =>
        GetBotProviderAssetBundleDownloadZipUrl(workspaceId, botProviderId, bundleName),
      downloadZipFileName: `${bundleName}.zip`,
    };
  }, [
    workspaceId,
    botProviderId,
    currentBundle.bundleName,
    isOperating,
    saveTextFileMutation,
    uploadFileMutation,
    deleteFileMutation,
    renameFileMutation,
    duplicateFileMutation,
    createFolderMutation,
    deleteFolderMutation,
    renameFolderMutation,
    duplicateFolderMutation,
    onBundleUpdated,
  ]);

  const extraActions =
    mode === 'manage' && onOpenManifestEditor ? (
      <Button
        variant="filled"
        color="indigo"
        size="xs"
        leftSection={<IconSettingsCode size={14} />}
        onClick={onOpenManifestEditor}
      >
        設定擴充槽
      </Button>
    ) : null;

  return (
    <VirtualFileExplorerModal
      opened={opened}
      onClose={onClose}
      title={title || `資產總管 — ${currentBundle.bundleName || ''}`}
      rootLabel="根目錄"
      files={currentBundle.files || []}
      initialSelectedFile={selectedFilePath}
      adapter={adapter}
      extraToolbarActions={extraActions}
      mode={mode}
      allowedExtensions={allowedExtensions}
      onSelectConfirm={onSelectConfirm}
    />
  );
}
